import { flightCurve, flightPoint, flightAngle, hitFlight } from './repairFlightGeometry'

// 一个 Canvas 承载全部飞线，地图只在视角变化时重新投影，动画不触发 React 渲染。
export function createRepairFlightLayer(map, onSelect) {
    class RepairFlightLayer extends window.TMap.DOMOverlay {
        onInit() {
            this.items = []
            this.curves = []
            this.visible = true
            this.disposed = false
            this.motion = window.matchMedia('(prefers-reduced-motion: reduce)')
            this.restart = () => this.start()
            this.click = event => {
                if (!this.visible || this.disposed) return
                const point = map.projectToContainer(event.latLng)
                const hit = [...this.curves].reverse().find(item => hitFlight(item.curve, point))
                if (hit) onSelect(hit.orderId)
            }
            map.on('click', this.click)
            this.motion.addEventListener('change', this.restart)
            document.addEventListener('visibilitychange', this.restart)
        }

        createDOM() {
            this.canvas = document.createElement('canvas')
            this.canvas.className = 'repair-flight-canvas'
            this.canvas.setAttribute('aria-hidden', 'true')
            this.canvas.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;'
            this.context = this.canvas.getContext('2d')
            return this.canvas
        }

        setData(items, selectedId) {
            this.items = items
            this.selectedId = selectedId
            this.updateDOM()
        }

        setVisible(visible) {
            this.visible = visible
            this.canvas.style.display = visible ? '' : 'none'
            if (visible) this.updateDOM()
            else cancelAnimationFrame(this.frame)
        }

        updateDOM() {
            if (!this.canvas || this.disposed || !this.visible) return
            const width = this.canvas.clientWidth, height = this.canvas.clientHeight
            const scale = Math.min(window.devicePixelRatio || 1, 2)
            if (this.canvas.width !== Math.round(width * scale) || this.canvas.height !== Math.round(height * scale)) {
                this.canvas.width = Math.round(width * scale)
                this.canvas.height = Math.round(height * scale)
            }
            this.context.setTransform(scale, 0, 0, scale, 0, 0)
            this.width = width
            this.height = height
            this.curves = this.items.map(item => {
                const start = map.projectToContainer(new window.TMap.LatLng(item.origin.lat, item.origin.lng))
                const end = map.projectToContainer(new window.TMap.LatLng(item.destination.lat, item.destination.lng))
                const curve = flightCurve(start, end)
                // 工单哈希错开动画，周期刷新不会让所有箭头同时跳回起点。
                const phase = Array.from(item.orderId).reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 0) % 4000
                return { orderId: item.orderId, curve, phase }
            }).filter(item => item.curve && this.inView(item.curve))
            // 选中飞线最后绘制，避免被其他工单遮挡。
            this.curves.sort((a, b) => Number(a.orderId === this.selectedId) - Number(b.orderId === this.selectedId))
            this.start()
        }

        inView({ start, end, control }) {
            const xs = [start.x, end.x, control.x], ys = [start.y, end.y, control.y]
            return Math.max(...xs) >= -40 && Math.min(...xs) <= this.width + 40
                && Math.max(...ys) >= -40 && Math.min(...ys) <= this.height + 40
        }

        start() {
            cancelAnimationFrame(this.frame)
            if (this.disposed || !this.context || !this.visible || document.hidden) return
            this.draw(performance.now())
            if (this.curves.length && !this.motion.matches) {
                const tick = time => {
                    this.draw(time)
                    this.frame = requestAnimationFrame(tick)
                }
                this.frame = requestAnimationFrame(tick)
            }
        }

        draw(time) {
            const ctx = this.context
            ctx.clearRect(0, 0, this.width, this.height)
            this.curves.forEach(({ orderId, curve, phase }) => {
                const selected = orderId === this.selectedId
                const muted = this.selectedId && !selected
                const color = selected ? '#ed7525' : '#2475ed'
                const { start, control, end } = curve
                ctx.lineCap = 'round'
                ctx.beginPath()
                ctx.moveTo(start.x, start.y)
                ctx.quadraticCurveTo(control.x, control.y, end.x, end.y)
                ctx.globalAlpha = muted ? .12 : .32
                ctx.strokeStyle = color
                ctx.lineWidth = selected ? 2.4 : 1.5
                ctx.stroke()

                // 箭头沿机构 → 维修地点单向循环，拖尾由后向前渐亮，保持弧形轨迹。
                const progress = this.motion.matches ? .72 : ((time + phase) % 4000) / 4000
                const head = .08 + progress * .84
                const tail = Math.max(.02, head - .19)
                ctx.strokeStyle = color
                ctx.lineWidth = selected ? 4 : 3
                for (let i = 0; i < 18; i += 1) {
                    const p1 = flightPoint(curve, tail + (head - tail) * i / 18)
                    const p2 = flightPoint(curve, tail + (head - tail) * (i + 1) / 18)
                    ctx.globalAlpha = (muted ? .12 : .95) * ((i + 1) / 18) ** 1.7
                    ctx.beginPath()
                    ctx.moveTo(p1.x, p1.y)
                    ctx.lineTo(p2.x, p2.y)
                    ctx.stroke()
                }
                const tip = flightPoint(curve, head)
                ctx.save()
                ctx.translate(tip.x, tip.y)
                ctx.rotate(flightAngle(curve, head))
                ctx.globalAlpha = muted ? .2 : 1
                ctx.fillStyle = color
                ctx.shadowColor = color
                ctx.shadowBlur = muted || this.motion.matches ? 0 : 6
                // 这是数据图形的箭头，不使用图片图标，箭头始终朝向曲线切线方向。
                ctx.beginPath()
                ctx.moveTo(8, 0)
                ctx.lineTo(-6, -5)
                ctx.lineTo(-3, 0)
                ctx.lineTo(-6, 5)
                ctx.closePath()
                ctx.fill()
                ctx.restore()
            })
            ctx.globalAlpha = 1
        }

        onDestroy() {
            if (this.disposed) return
            this.disposed = true
            cancelAnimationFrame(this.frame)
            map.off('click', this.click)
            this.motion.removeEventListener('change', this.restart)
            document.removeEventListener('visibilitychange', this.restart)
            this.items = []
            this.curves = []
        }
    }
    return new RepairFlightLayer({ map })
}
